import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import SearchIcon from '../SearchIcon';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';
import { CartIcon, FavoriteIcon, UserIcon, LanguageSwitcher } from '../../../../components';

import css from './TopbarDesktop.module.css';

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const SignupLink = () => {
  return (
    <NamedLink id="signup-link" name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink id="login-link" name="LoginPage" className={css.loginButton}>
      <FormattedMessage id="TopbarDesktop.login" />
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, inboxTab }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      id="inbox-link"
      className={css.topbarLink}
      name="InboxPage"
      params={{ tab: inboxTab }}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout, showManageListingsLink, intl, isAdmin, inboxTab }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage) && currentPage !== 'SubscriptionPage';
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const selling_blocked = currentUser?.attributes?.profile?.publicData?.selling_blocked;

  return (
    <Menu skipFocusOnNavigation={true}>
      <MenuLabel
        id="profile-menu-label"
        // className={css.profileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.profileMenu' })}
      >
        <UserIcon className={css.userIconLink} />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="ManageListingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.yourListingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="FavoritesPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('FavoritesPage'))}
            name="FavoritesPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.favoritesLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="InboxPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('InboxPage'))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.inbox" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="SubscriptionPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('SubscriptionPage'))}
            name="SubscriptionPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.SubscriptionLink" />
          </NamedLink>
        </MenuItem>
        {isAdmin && (
          <MenuItem key="AdminWhiteGlovePage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('AdminWhiteGlovePage'))}
              name="AdminWhiteGlovePage"
            >
              <span className={css.menuItemBorder} />
              White Glove Service
            </NamedLink>
          </MenuItem>
        )}
        {isAdmin && (
          <MenuItem key="AmbassadorCommissionPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('AmbassadorCommissionPage'))}
              name="AmbassadorCommissionPage"
            >
              <span className={css.menuItemBorder} />
              Embajadores
            </NamedLink>
          </MenuItem>
        )}
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    isAtListingLimit,
    inboxTab,
    isAdmin,
  } = props;

  const [mounted, setMounted] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const classes = classNames(rootClassName || css.root, className, {
    [css.scrolled]: isScrolled,
  });

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink notificationCount={notificationCount} inboxTab={inboxTab} />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
      intl={intl}
      isAdmin={isAdmin}
      inboxTab={inboxTab}
    />
  ) : null;

  const signupLinkMaybe = null;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const handleSearchSubmit = values => {
    onSearchSubmit(values);
    setSearchOpen(false);
  };

  return (
    <nav
      className={classes}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
    >
      <LinkedLogo
        id="logo-topbar-desktop"
        className={css.logoLink}
        layout="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        linkToExternalSite={config?.topbar?.logoLink}
      />

      {/* Nav links — centered */}
      <div className={css.navWrapper}>
        <CustomLinksMenu
          currentPage={currentPage}
          customLinks={customLinks}
          intl={intl}
          hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
          showCreateListingsLink={showCreateListingsLink}
          isAtListingLimit={isAtListingLimit}
        />
      </div>

      {/* Right section: search toggle + auth */}
      <div className={css.rightSection}>
        {showSearchForm ? (
          <div className={css.searchSection} role="search">
            {searchOpen ? (
              <TopbarSearchForm
                className={css.expandedSearchForm}
                desktopInputRoot={css.topbarSearchInput}
                onSubmit={handleSearchSubmit}
                initialValues={initialSearchFormValues}
                appConfig={config}
              />
            ) : null}
            <button
              className={css.searchIconBtn}
              onClick={() => setSearchOpen(open => !open)}
              aria-expanded={searchOpen}
              aria-label={
                searchOpen
                  ? intl.formatMessage({ id: 'TopbarDesktop.closeSearch' })
                  : intl.formatMessage({ id: 'TopbarDesktop.searchIcon' }, { marketplaceName })
              }
            >
              {searchOpen ? (
                <CloseIcon aria-hidden="true" />
              ) : (
                <SearchIcon
                  rootClassName={css.searchIconSvg}
                  aria-hidden="true"
                  ariaLabel={
                    searchOpen
                      ? intl.formatMessage({ id: 'TopbarDesktop.closeSearch' })
                      : intl.formatMessage({ id: 'TopbarDesktop.searchIcon' }, { marketplaceName })
                  }
                />
              )}
            </button>
          </div>
        ) : null}

        {authenticatedOnClientSide ? (
          <FavoriteIcon intl={intl} />
        ) : null}

        {/* {inboxLinkMaybe} */}
        {profileMenuMaybe}
        {authenticatedOnClientSide ? <CartIcon intl={intl} /> : null}
        {/* <LanguageSwitcher /> */}
        {signupLinkMaybe}
        {loginLinkMaybe}
      </div>
    </nav>
  );
};

export default TopbarDesktop;
